import i18next from 'i18next';

/**
 * Translates a key within the clients feature namespace.
 *
 * @param key - The translation key (relative to the `features/clients` namespace).
 * @returns The translated string.
 */
const t = (key: string): string => i18next.t(key, { ns: 'features/clients' });

/**
 * Resolves the localized labels for the two client types.
 *
 * @returns A map of client-type keys to their translated display labels.
 */
export function getTypeLabels(): Record<string, string> {
  return {
    individual: t('helpers.typeLabels.individual'),
    company: t('helpers.typeLabels.company'),
  };
}

/**
 * Resolves the localized labels for each client preference field.
 *
 * @returns A map of preference keys to their translated display labels.
 */
export function getPrefLabels(): Record<string, string> {
  return {
    business_type: t('helpers.prefLabels.business_type'),
    cities: t('helpers.prefLabels.cities'),
    neighborhoods: t('helpers.prefLabels.neighborhoods'),
    price_min: t('helpers.prefLabels.price_from'),
    price_max: t('helpers.prefLabels.price_to'),
    property_kinds: t('helpers.prefLabels.property_kinds'),
    // The four the wizard writes but the map never named. Without them
    // the chips read `area_min: 157`, the storage key verbatim.
    area_min: t('helpers.prefLabels.area_min'),
    area_max: t('helpers.prefLabels.area_max'),
    bedrooms_min: t('helpers.prefLabels.bedrooms_min'),
    parking_min: t('helpers.prefLabels.parking_min'),
    bathrooms_min: t('form.labels.bathroomsMin'),
    suites_min: t('form.labels.suitesMin'),
    furnished: t('form.labels.furnished'),
    pet_friendly: t('form.labels.petFriendly'),
    amenities_internal: t('form.labels.amenitiesInternal'),
    amenities_external: t('form.labels.amenitiesExternal'),
    amenities_security: t('form.labels.amenitiesSecurity'),
    condo_leisure: t('form.labels.condoLeisure'),
    condo_security: t('form.labels.condoSecurity'),
    tags: t('form.labels.preferenceTags'),
  };
}

/**
 * Resolves the localized labels for each business-type option.
 *
 * @returns A map of business-type keys to their translated display labels.
 */
export function getBusinessTypeLabels(): Record<string, string> {
  return {
    buy: t('helpers.businessTypes.buy'),
    sale: t('helpers.businessTypes.sale'),
    rent: t('helpers.businessTypes.rent'),
    both: t('helpers.businessTypes.both'),
    swap: t('helpers.businessTypes.swap'),
    exchange: t('helpers.businessTypes.swap'),
    short_term: t('helpers.businessTypes.short_term'),
  };
}

/**
 * Resolves the localized labels for each supported property kind. Keys are the
 * canonical accented category values (plus legacy underscored aliases) that map
 * to the same i18n label.
 *
 * @returns A map of property-kind keys to their translated display labels.
 */
export function getPropertyKindLabels(): Record<string, string> {
  return {
    Casa: t('helpers.propertyKinds.Casa'),
    Apartamento: t('helpers.propertyKinds.Apartamento'),
    Terreno: t('helpers.propertyKinds.Terreno'),
    'Prédio Comercial': t('helpers.propertyKinds.Predio_Comercial'),
    'Sala Comercial': t('helpers.propertyKinds.Sala_Comercial'),
    'Galpão': t('helpers.propertyKinds.Galpao'),
    Kitnet: t('helpers.propertyKinds.Kitnet'),
    Studio: t('helpers.propertyKinds.Studio'),
    Flat: t('helpers.propertyKinds.Flat'),
    Loft: t('helpers.propertyKinds.Loft'),
    Cobertura: t('helpers.propertyKinds.Cobertura'),
    'Chácara': t('helpers.propertyKinds.Chacara'),
    'Casa Comercial': t('helpers.propertyKinds.Casa_Comercial'),
    'Ponto Comercial': t('helpers.propertyKinds.Ponto_Comercial'),
    Garagem: t('helpers.propertyKinds.Garagem'),
    Fazenda: t('helpers.propertyKinds.Fazenda'),
    'Sítio': t('helpers.propertyKinds.Sitio'),
    Pousada: t('helpers.propertyKinds.Pousada'),
    Academia: t('helpers.propertyKinds.Academia'),
    'Edifício': t('helpers.propertyKinds.Edificio'),
    Outro: t('helpers.propertyKinds.Outro'),
    Predio_Comercial: t('helpers.propertyKinds.Predio_Comercial'),
    Sala_Comercial: t('helpers.propertyKinds.Sala_Comercial'),
    Galpao: t('helpers.propertyKinds.Galpao'),
    Chacara: t('helpers.propertyKinds.Chacara'),
    Casa_Comercial: t('helpers.propertyKinds.Casa_Comercial'),
    Ponto_Comercial: t('helpers.propertyKinds.Ponto_Comercial'),
    Sitio: t('helpers.propertyKinds.Sitio'),
  };
}
